import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Chip,
  Button,
  IconButton,
  TextField,
  Autocomplete,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Add as AddIcon,
  Remove as RemoveIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { WorkoutPlanService } from '../../services/workoutPlanService';
import { ExerciseService } from '../../services/exerciseService';
import type {
  WorkoutPlanDetail,
  WorkoutPlanExerciseDetail,
  PlannedSetDetail,
  CreateWorkoutPlanInput,
  Exercise,
} from '../../services/types';

interface PlannedSessionEditorProps {
  planData: WorkoutPlanDetail;
  sessionId: number;
  onSave: (updatedPlan: WorkoutPlanDetail) => void;
  onDelete: () => void;
}

interface EditableSet {
  targetReps: number;
  targetWeight: number | null;
  targetRpe: number | null;
}

interface EditableExercise {
  exerciseId: number;
  exerciseName: string;
  muscleGroup: string;
  isSubstitution: boolean | null;
  sets: EditableSet[];
}

const PlannedSessionEditor: React.FC<PlannedSessionEditorProps> = ({
  planData,
  sessionId,
  onSave,
  onDelete,
}) => {
  const [editing, setEditing] = useState(false);
  const [exercises, setExercises] = useState<EditableExercise[]>([]);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [addExerciseOpen, setAddExerciseOpen] = useState(false);
  const [allExercises, setAllExercises] = useState<Exercise[]>([]);
  const [exercisesLoading, setExercisesLoading] = useState(false);

  const initEditState = () => {
    setExercises(
      planData.exercises.map((ex) => ({
        exerciseId: ex.exerciseId,
        exerciseName: ex.exerciseName,
        muscleGroup: ex.muscleGroup,
        isSubstitution: ex.isSubstitution,
        sets: ex.sets.map((s) => ({
          targetReps: s.targetReps,
          targetWeight: s.targetWeight,
          targetRpe: s.targetRpe,
        })),
      }))
    );
  };

  const handleStartEdit = () => {
    initEditState();
    setEditing(true);
  };

  const handleCancel = () => {
    setEditing(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: CreateWorkoutPlanInput = {
        name: planData.name,
        workoutDate: planData.workoutDate,
        programId: planData.programId ?? undefined,
        dayNumber: planData.dayNumber ?? undefined,
        exercises: exercises.map((ex) => ({
          exerciseId: ex.exerciseId,
          isSubstitution: ex.isSubstitution ?? false,
          sets: ex.sets.map((s, idx) => ({
            setOrder: idx + 1,
            targetReps: s.targetReps,
            targetWeight: s.targetWeight,
            targetRpe: s.targetRpe,
          })),
        })),
      };

      await WorkoutPlanService.update(sessionId, payload);
      const updated = await WorkoutPlanService.getById(sessionId);
      setEditing(false);
      onSave(updated);
    } catch (err) {
      console.error('Error saving plan:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await WorkoutPlanService.delete(sessionId);
      onDelete();
    } catch (err) {
      console.error('Error deleting plan:', err);
    } finally {
      setDeleting(false);
    }
  };

  // Set manipulation
  const updateSet = (exIdx: number, setIdx: number, field: keyof EditableSet, value: number | null) => {
    setExercises((prev) => {
      const copy = [...prev];
      copy[exIdx] = {
        ...copy[exIdx],
        sets: copy[exIdx].sets.map((s, i) =>
          i === setIdx ? { ...s, [field]: value } : s
        ),
      };
      return copy;
    });
  };

  const addSet = (exIdx: number) => {
    setExercises((prev) => {
      const copy = [...prev];
      const lastSet = copy[exIdx].sets[copy[exIdx].sets.length - 1];
      copy[exIdx] = {
        ...copy[exIdx],
        sets: [
          ...copy[exIdx].sets,
          {
            targetReps: lastSet?.targetReps ?? 10,
            targetWeight: lastSet?.targetWeight ?? 0,
            targetRpe: lastSet?.targetRpe ?? 7,
          },
        ],
      };
      return copy;
    });
  };

  const removeSet = (exIdx: number, setIdx: number) => {
    setExercises((prev) => {
      const copy = [...prev];
      copy[exIdx] = {
        ...copy[exIdx],
        sets: copy[exIdx].sets.filter((_, i) => i !== setIdx),
      };
      return copy;
    });
  };

  // Exercise manipulation
  const removeExercise = (exIdx: number) => {
    setExercises((prev) => prev.filter((_, i) => i !== exIdx));
  };

  const handleOpenAddExercise = async () => {
    setAddExerciseOpen(true);
    if (allExercises.length === 0) {
      setExercisesLoading(true);
      try {
        const list = await ExerciseService.getAll();
        setAllExercises(list);
      } catch (err) {
        console.error('Error loading exercises:', err);
      } finally {
        setExercisesLoading(false);
      }
    }
  };

  const handleAddExercise = (exercise: Exercise | null) => {
    if (!exercise) return;
    setExercises((prev) => [
      ...prev,
      {
        exerciseId: exercise.id,
        exerciseName: exercise.name,
        muscleGroup: exercise.muscleGroup,
        isSubstitution: false,
        sets: [
          { targetReps: 10, targetWeight: 0, targetRpe: 7 },
          { targetReps: 10, targetWeight: 0, targetRpe: 7 },
          { targetReps: 10, targetWeight: 0, targetRpe: 7 },
        ],
      },
    ]);
    setAddExerciseOpen(false);
  };

  // ── VIEW MODE ──
  if (!editing) {
    return (
      <Box>
        {planData.exercises.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 1 }}>
            Планът няма добавени упражнения.
          </Typography>
        ) : (
          planData.exercises.map((ex, exIdx) => (
            <Box key={exIdx} sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                <Typography variant="body1" fontWeight="bold">
                  {ex.exerciseName}
                </Typography>
                <Chip
                  label={ex.muscleGroup}
                  size="small"
                  sx={{
                    bgcolor: ExerciseService.getMuscleGroupColor(ex.muscleGroup),
                    color: '#fff',
                    fontSize: '0.7rem',
                    height: 20,
                  }}
                />
              </Box>
              {ex.sets.map((set, setIdx) => (
                <Typography key={setIdx} variant="body2" color="text.secondary" sx={{ pl: 1 }}>
                  Серия {setIdx + 1}: {set.targetReps} x {set.targetWeight ?? 0}kg
                  {set.targetRpe ? ` (RPE ${set.targetRpe})` : ''}
                </Typography>
              ))}
            </Box>
          ))
        )}
        <Box sx={{ display: 'flex', gap: 1, mt: 2, flexWrap: 'wrap' }}>
          <Button
            size="small"
            variant="outlined"
            startIcon={<EditIcon />}
            onClick={handleStartEdit}
          >
            Редактирай план
          </Button>
          <Button
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={() => setConfirmDelete(true)}
          >
            Изтрий план
          </Button>
        </Box>

        {/* Delete confirmation */}
        {confirmDelete && (
          <Box sx={{ mt: 1, display: 'flex', gap: 1, alignItems: 'center' }}>
            <Typography variant="body2" color="error">
              Сигурни ли сте?
            </Typography>
            <Button
              size="small"
              variant="contained"
              color="error"
              disabled={deleting}
              onClick={handleDelete}
            >
              {deleting ? <CircularProgress size={16} /> : 'Да, изтрий'}
            </Button>
            <Button size="small" onClick={() => setConfirmDelete(false)} disabled={deleting}>
              Откажи
            </Button>
          </Box>
        )}
      </Box>
    );
  }

  // ── EDIT MODE ──
  return (
    <Box>
      {exercises.map((ex, exIdx) => (
        <Box key={exIdx} sx={{ mb: 2, p: 1.5, border: '1px solid', borderColor: 'divider', borderRadius: 1 }}>
          {/* Exercise header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1" fontWeight="bold">
                {ex.exerciseName}
              </Typography>
              <Chip
                label={ex.muscleGroup}
                size="small"
                sx={{
                  bgcolor: ExerciseService.getMuscleGroupColor(ex.muscleGroup),
                  color: '#fff',
                  fontSize: '0.7rem',
                  height: 20,
                }}
              />
            </Box>
            <IconButton
              size="small"
              color="error"
              onClick={() => removeExercise(exIdx)}
              title="Премахни упражнение"
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>

          {/* Sets */}
          {ex.sets.map((set, setIdx) => (
            <Box key={setIdx} sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
              <Typography variant="body2" sx={{ minWidth: 55 }}>
                Серия {setIdx + 1}:
              </Typography>

              {/* Weight controls */}
              <IconButton
                size="small"
                onClick={() => updateSet(exIdx, setIdx, 'targetWeight', Math.max(0, (set.targetWeight ?? 0) - 2.5))}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
              <TextField
                size="small"
                type="number"
                value={set.targetWeight ?? 0}
                onChange={(e) => updateSet(exIdx, setIdx, 'targetWeight', parseFloat(e.target.value) || 0)}
                sx={{ width: 70 }}
                slotProps={{ htmlInput: { step: 2.5, min: 0 } }}
              />
              <Typography variant="body2" sx={{ mr: 0.5 }}>kg</Typography>
              <IconButton
                size="small"
                onClick={() => updateSet(exIdx, setIdx, 'targetWeight', (set.targetWeight ?? 0) + 2.5)}
              >
                <AddIcon fontSize="small" />
              </IconButton>

              {/* Reps controls */}
              <IconButton
                size="small"
                onClick={() => updateSet(exIdx, setIdx, 'targetReps', Math.max(1, set.targetReps - 1))}
              >
                <RemoveIcon fontSize="small" />
              </IconButton>
              <TextField
                size="small"
                type="number"
                value={set.targetReps}
                onChange={(e) => updateSet(exIdx, setIdx, 'targetReps', Math.max(1, parseInt(e.target.value) || 1))}
                sx={{ width: 55 }}
                slotProps={{ htmlInput: { step: 1, min: 1 } }}
              />
              <Typography variant="body2" sx={{ mr: 0.5 }}>повт.</Typography>
              <IconButton
                size="small"
                onClick={() => updateSet(exIdx, setIdx, 'targetReps', set.targetReps + 1)}
              >
                <AddIcon fontSize="small" />
              </IconButton>

              {/* Remove set */}
              <IconButton
                size="small"
                color="error"
                disabled={ex.sets.length <= 1}
                onClick={() => removeSet(exIdx, setIdx)}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))}

          <Button size="small" startIcon={<AddIcon />} onClick={() => addSet(exIdx)} sx={{ mt: 0.5 }}>
            Добави серия
          </Button>
        </Box>
      ))}

      {/* Add exercise button */}
      <Button
        size="small"
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={handleOpenAddExercise}
        sx={{ mb: 2 }}
      >
        Добави упражнение
      </Button>

      {/* Save / Cancel */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button
          size="small"
          variant="contained"
          disabled={saving || exercises.length === 0}
          onClick={handleSave}
        >
          {saving ? <CircularProgress size={16} /> : 'Запази'}
        </Button>
        <Button size="small" disabled={saving} onClick={handleCancel}>
          Откажи
        </Button>
      </Box>

      {/* Add exercise dialog */}
      <Dialog open={addExerciseOpen} onClose={() => setAddExerciseOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Добави упражнение</DialogTitle>
        <DialogContent>
          {exercisesLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Autocomplete
              options={allExercises}
              getOptionLabel={(opt) => `${opt.name} (${opt.muscleGroup})`}
              onChange={(_, value) => handleAddExercise(value)}
              renderInput={(params) => (
                <TextField {...params} label="Търси упражнение" autoFocus sx={{ mt: 1 }} />
              )}
              noOptionsText="Няма резултати"
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddExerciseOpen(false)}>Затвори</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlannedSessionEditor;
